import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createClosedDealAttachment,
  downloadClosedDealAttachment,
  getClosedDealKycStatus,
  reverifyClosedDealAttachmentKyc,
  uploadFilesToS3
} from '../../services/api/implementation/impl';
import {
  evaluateKycCompleteness,
  evaluateKycVerification,
  kycVerifyBadgeClasses,
  KYC_CATEGORIES,
  KYC_CATEGORY_LABELS,
  matchStatusLabel,
  normalizeKycCategory,
  pickLatestKycAttachment,
  SECONDARY_ID_OPTIONS
} from './postSalesKyc';

const SLOTS = [
  {
    category: KYC_CATEGORIES.PAN,
    label: KYC_CATEGORY_LABELS.pan,
    required: true,
    hint: 'Required for every buyer — number must match Application PAN'
  }
];

const DealKycDocumentsSection = ({
  dealId,
  attachments = [],
  onUpdated,
  readOnly = false
}) => {
  const [secondaryChoice, setSecondaryChoice] = useState(KYC_CATEGORIES.AADHAAR);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const fileRefs = useRef({});

  const kyc = evaluateKycCompleteness(attachments);

  const {
    data: kycStatusRaw,
    refetch: refetchKycStatus,
    isFetching: kycStatusFetching
  } = useQuery({
    queryKey: ['closedDealKycStatus', dealId],
    queryFn: () => getClosedDealKycStatus(dealId),
    enabled: Boolean(dealId),
    refetchInterval: (query) => {
      const status = query.state.data?.kyc_verify_status;
      return status === 'extracting' ? 3000 : false;
    }
  });

  const verification = useMemo(
    () => evaluateKycVerification(kycStatusRaw),
    [kycStatusRaw]
  );

  useEffect(() => {
    if (kyc.hasAadhaar && !kyc.hasVoter) {
      setSecondaryChoice(KYC_CATEGORIES.AADHAAR);
    } else if (kyc.hasVoter && !kyc.hasAadhaar) {
      setSecondaryChoice(KYC_CATEGORIES.VOTER_ID);
    }
  }, [kyc.hasAadhaar, kyc.hasVoter]);

  // Re-uploads pile up as extra rows; only the newest one is shown/verified.
  const latestByCategory = (cat) =>
    pickLatestKycAttachment(attachments.filter((a) => normalizeKycCategory(a) === cat));

  // Same rule as the backend roll-up: the newest document of the slot wins.
  const docMatchByCategory = (cat) =>
    pickLatestKycAttachment(
      (verification.documents || []).filter(
        (d) => String(d.category).toLowerCase() === cat
      )
    );

  const uploadMutation = useMutation({
    mutationFn: async ({ file, category, label }) => {
      setUploadingSlot(category);
      const uploaded = await uploadFilesToS3([file]);
      if (!uploaded?.length) throw new Error('Upload failed');
      const fileMeta = uploaded[0];
      return createClosedDealAttachment({
        deal_id: dealId,
        name: label,
        url: fileMeta.url,
        mime_type: file.type,
        size_bytes: file.size,
        category
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded — verifying against Application…');
      onUpdated?.();
      refetchKycStatus();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    },
    onSettled: () => setUploadingSlot(null)
  });

  const downloadMutation = useMutation({
    mutationFn: (id) => downloadClosedDealAttachment(id),
    onSuccess: ({ data, filename, contentType }) => {
      const blob = new Blob([data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'kyc-document';
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Download failed');
    }
  });

  const reverifyMutation = useMutation({
    mutationFn: (id) => reverifyClosedDealAttachmentKyc(id),
    onSuccess: () => {
      toast.success('Re-verification finished');
      onUpdated?.();
      refetchKycStatus();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Reverify failed');
    }
  });

  const pickFile = (category, label) => {
    if (readOnly) return;
    const input = fileRefs.current[category];
    if (!input) return;
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) uploadMutation.mutate({ file, category, label });
      e.target.value = '';
    };
    input.click();
  };

  const secondaryCategory = secondaryChoice;
  const secondaryLabel =
    SECONDARY_ID_OPTIONS.find((o) => o.id === secondaryCategory)?.label || 'ID proof';

  const missingApplicantHint =
    !verification.applicant?.has_pan || !verification.applicant?.has_aadhar
      ? 'Fill primary applicant PAN & Aadhaar in Applicants before verification can succeed.'
      : null;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900">KYC documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            PAN is required. Upload Aadhaar for number verification (Voter ID counts as
            uploaded but not as verified).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              kyc.complete
                ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'
            }`}>
            {kyc.complete ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            ) : (
              <AlertCircle className="h-4 w-4" aria-hidden />
            )}
            {kyc.complete ? 'Docs uploaded' : `Missing: ${kyc.missing.join(', ')}`}
          </div>
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${kycVerifyBadgeClasses(
              verification.status
            )}`}>
            {verification.extracting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : verification.verified ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            ) : (
              <AlertCircle className="h-4 w-4" aria-hidden />
            )}
            {verification.label}
          </div>
        </div>
      </div>

      {missingApplicantHint && (
        <div className="mx-4 mt-3 rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 sm:mx-5">
          {missingApplicantHint}
        </div>
      )}

      <div className="space-y-3 p-4 sm:p-5">
        {SLOTS.map((slot) => (
          <KycSlotRow
            key={slot.category}
            slot={slot}
            file={latestByCategory(slot.category)}
            matchDoc={docMatchByCategory(slot.category)}
            uploading={uploadingSlot === slot.category}
            readOnly={readOnly}
            reverifyPending={reverifyMutation.isPending}
            onUpload={() => pickFile(slot.category, slot.label)}
            onDownload={(id) => downloadMutation.mutate(id)}
            onReverify={(id) => reverifyMutation.mutate(id)}
            inputRef={(el) => {
              fileRefs.current[slot.category] = el;
            }}
          />
        ))}

        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Second ID proof <span className="text-red-600">*</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Choose Aadhaar for auto number match. Voter ID is accepted for upload completeness
            only.
          </p>
          {!readOnly && (
            <div className="mt-3 flex flex-wrap gap-3">
              {SECONDARY_ID_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className="inline-flex cursor-pointer items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium hover:border-[#2A565E]/40">
                  <input
                    type="radio"
                    name="secondary-id"
                    className="h-4 w-4 accent-[#2A565E]"
                    checked={secondaryChoice === opt.id}
                    onChange={() => setSecondaryChoice(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
          <div className="mt-3">
            <KycSlotRow
              slot={{
                category: secondaryCategory,
                label: secondaryLabel,
                required: true,
                hint: `Upload ${secondaryLabel}`
              }}
              file={latestByCategory(secondaryCategory)}
              matchDoc={docMatchByCategory(secondaryCategory)}
              uploading={uploadingSlot === secondaryCategory}
              readOnly={readOnly}
              reverifyPending={reverifyMutation.isPending}
              onUpload={() => pickFile(secondaryCategory, secondaryLabel)}
              onDownload={(id) => downloadMutation.mutate(id)}
              onReverify={(id) => reverifyMutation.mutate(id)}
              inputRef={(el) => {
                fileRefs.current[secondaryCategory] = el;
              }}
              compact
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * The tick beside a file used to be hard-coded green, so a document whose
 * verification had failed still showed a green tick next to the word "Failed".
 * The icon follows the result now.
 */
function StatusIcon({ tone, hasMatch }) {
  if (!hasMatch) {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />;
  }
  if (tone === 'emerald') {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />;
  }
  if (tone === 'rose') {
    return <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />;
  }
  if (tone === 'amber') {
    return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-600" aria-hidden />;
  }
  return <CheckCircle2 className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />;
}

function KycSlotRow({
  slot,
  file,
  matchDoc,
  uploading,
  readOnly,
  reverifyPending,
  onUpload,
  onDownload,
  onReverify,
  inputRef,
  compact = false
}) {
  const hasFile = Boolean(file);
  const matchInfo = matchStatusLabel(matchDoc?.kyc_match_status);
  const canReverify =
    !readOnly &&
    hasFile &&
    (slot.category === KYC_CATEGORIES.PAN || slot.category === KYC_CATEGORIES.AADHAAR);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 ${
        compact ? 'bg-white p-3' : 'bg-gray-50/40 p-4'
      }`}>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">
          {slot.label}
          {slot.required && <span className="ml-1 text-red-500">*</span>}
        </p>
        {!compact && <p className="mt-0.5 text-sm text-gray-500">{slot.hint}</p>}
        {hasFile && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-gray-50 px-3 py-2">
            <StatusIcon tone={matchInfo.tone} hasMatch={Boolean(matchDoc)} />
            <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{file.name}</span>
            {matchDoc && (
              <span
                className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                  matchInfo.tone === 'emerald'
                    ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                    : matchInfo.tone === 'rose'
                      ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
                      : matchInfo.tone === 'amber'
                        ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'
                        : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                }`}>
                {matchInfo.label}
                {matchDoc.extracted_last4 ? ` · …${matchDoc.extracted_last4}` : ''}
              </span>
            )}
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload(file.id)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-[#2A565E]/40 hover:text-[#2A565E] focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1">
                <Download className="h-4 w-4" aria-hidden />
                Download
              </button>
              {canReverify && (
                <button
                  type="button"
                  disabled={reverifyPending}
                  onClick={() => onReverify(file.id)}
                  title="Read the document again and re-check it against the applicant record"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-[#2A565E]/40 hover:text-[#2A565E] focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:opacity-50">
                  {reverifyPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-4 w-4" aria-hidden />
                  )}
                  Re-verify
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {!readOnly && (
        <button
          type="button"
          disabled={uploading}
          onClick={onUpload}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#2A565E]/30 bg-white px-3.5 py-2.5 text-sm font-semibold text-[#2A565E] hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:opacity-50">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Upload className="h-4 w-4" aria-hidden />
          )}
          {hasFile ? 'Replace' : 'Upload'}
        </button>
      )}
    </div>
  );
}

export default DealKycDocumentsSection;
